import { localeToLanguage } from 'wanderu-i18n';
import { localeConfig } from 'wanderu-shellapp';
import {
    removeDiacritics,
    removeLanguagePath,
} from 'wanderu-utils';

const BASE_CANONICAL_URL = 'https://www.wanderu.com';
const HOMEPAGE_PATH = '/';
const NON_LOCALE_HREF_LANGS = ['en', 'en-us', 'x-default'];
const LANG_TO_LOCALE_ORDER = {
    en: ['en-us', 'en-ca', 'en-gb'],
    es: ['es-es', 'es-mx'],
    fr: ['fr-fr', 'fr-ca'],
    it: ['it-it'],
    nl: ['nl-nl'],
    de: ['de-de'],
};

function buildCanonicalLink(opts) {
    const {
        locale,
        path,
    } = opts;

    const nonLocalizedPath = removeDiacritics(removeLanguagePath(path));

    return locale
        ? `${BASE_CANONICAL_URL}/${locale}${nonLocalizedPath}`
        : `${BASE_CANONICAL_URL}${nonLocalizedPath}`;
}

function buildHrefLangLink(opts) {
    const {
        hrefLang,
        locale,
        path,
    } = opts;

    const isNonLocale = isNonLocaleHrefLang({ hrefLang, path });
    return buildCanonicalLink({
        locale: isNonLocale
            ? undefined
            : locale,
        path,
    });
}

function isNonLocaleHrefLang({ hrefLang, path }) {
    const nonLocalizedPath = removeDiacritics(removeLanguagePath(path));
    const isHomePage = nonLocalizedPath === HOMEPAGE_PATH;
    const isNonLocale = NON_LOCALE_HREF_LANGS.includes(hrefLang);

    return isHomePage && isNonLocale;
}

function extractRegionAgnosticLangs(supportedLocales) {
    const langs = supportedLocales.map((locale) => localeToLanguage(locale));
    return [...new Set(langs)];
}

function getBestLocaleForLanguage(language, supportedLocales) {
    return LANG_TO_LOCALE_ORDER[language]?.find(locale => supportedLocales.includes(locale));
}

function getPseudoHrefLangPairs({
    regionAgnosticLangs,
    supportedLocales,
}) {
    let pseudoHrefLangPairs = [];
    if (regionAgnosticLangs.includes('es')) {
        pseudoHrefLangPairs.push({
            hrefLang: 'es-us',
            locale: supportedLocales.includes('es-mx')
                ? 'es-mx'
                : getBestLocaleForLanguage('es', supportedLocales),
        });
    }

    return pseudoHrefLangPairs;
}

function getHrefLangPairs({
    supportedLocales = [],
    noIndexLocale = [],
} = {}) {
    const regionAgnosticLangs = extractRegionAgnosticLangs(supportedLocales);
    const localeHrefLangPairs = supportedLocales.map(locale => ({ hrefLang: locale, locale }));

    const regionHrefLangPairs = regionAgnosticLangs.map(language => ({
        hrefLang: language,
        locale: getBestLocaleForLanguage(language, supportedLocales),
    }));

    const pseudoHrefLangPairs = getPseudoHrefLangPairs({
        regionAgnosticLangs,
        supportedLocales,
    });

    let hrefLangsPairs = [
        ...localeHrefLangPairs,
        ...pseudoHrefLangPairs,
        ...regionHrefLangPairs,
    ].filter(({ hrefLang, locale }) => !!locale && !!hrefLang);

    if (supportedLocales.includes(localeConfig.defaultLocale)) {
        hrefLangsPairs.push({
            hrefLang: 'x-default',
            locale: localeConfig.defaultLocale,
        });
    }

    return hrefLangsPairs.filter(hreflang => {
        return !noIndexLocale.includes(hreflang.locale);
    });
}

export {
    buildCanonicalLink,
    buildHrefLangLink,
    getHrefLangPairs,
};
